import { z } from 'zod'
import { moneySchema, optionalText } from './common.js'
import { compareMoney, toMoney } from './money.js'
import {
  assigneeKey,
  WORKFLOW_MAX_ASSIGNEES,
  type WorkflowAssignee,
  type WorkflowCondition,
  type WorkflowOperator,
  type WorkflowSpecInput,
  type WorkflowSubject,
  workflowAssigneeSchema,
} from './workflows.js'

/**
 * Built-in workflow templates (ADR-020): common approval shapes shipped with the app. A template cannot
 * name a company's roles or users (they are tenant data), so it has **slots** ("Finance approvers")
 * that the administrator fills with their own roles or users, and **amount parameters** ("above
 * 1,000.000") with defaults. Applying one creates an ordinary workflow with a draft; nothing is
 * published or routed until the administrator reviews, tests and publishes it.
 */

/** A rule whose value is one of the template's amount parameters. */
export interface WorkflowTemplateRule {
  field: string
  operator: Extract<WorkflowOperator, 'gt' | 'gte'>
  param: string
}

export interface WorkflowTemplateSlot {
  key: string
  en: string
  ar: string
  hintEn: string
  hintAr: string
}

export interface WorkflowTemplateParam {
  key: string
  en: string
  ar: string
  /** OMR, 3 decimals. */
  default: string
  /** This amount must be more than that parameter (e.g. the finance level above the manager level). */
  above?: string
}

export interface WorkflowTemplateStep {
  key: string
  en: string
  ar: string
  /** Who approves: the roles and users the administrator puts in this slot. */
  slot: string
  completion: 'ANY' | 'ALL'
  /** The step runs only when this holds; null = always. */
  when: WorkflowTemplateRule | null
}

export interface WorkflowTemplate {
  key: string
  subject: WorkflowSubject
  en: string
  ar: string
  descriptionEn: string
  descriptionAr: string
  /** Which submissions the workflow applies to; null = every submission. */
  appliesWhen: WorkflowTemplateRule | null
  params: readonly WorkflowTemplateParam[]
  slots: readonly WorkflowTemplateSlot[]
  steps: readonly WorkflowTemplateStep[]
}

const threshold = (dflt: string): WorkflowTemplateParam => ({
  key: 'threshold',
  en: 'Applies to purchases above (total including VAT)',
  ar: 'ينطبق على المشتريات التي تزيد عن (الإجمالي شامل الضريبة)',
  default: dflt,
})
const aboveThreshold: WorkflowTemplateRule = { field: 'total', operator: 'gt', param: 'threshold' }

const slot = (key: string, en: string, ar: string, hintEn: string, hintAr: string): WorkflowTemplateSlot => ({
  key,
  en,
  ar,
  hintEn,
  hintAr,
})
const ANY_ONE_HINT = {
  en: 'Any one of the people in these roles or named here can approve.',
  ar: 'يمكن لأي شخص في هذه الأدوار أو من الأشخاص المحددين هنا الاعتماد.',
}

export const WORKFLOW_TEMPLATES: readonly WorkflowTemplate[] = [
  {
    key: 'purchase-one-approver',
    subject: 'purchase',
    en: 'One approver above an amount',
    ar: 'معتمد واحد فوق مبلغ معين',
    descriptionEn: 'Purchases above the amount wait for one approval before they can be posted.',
    descriptionAr: 'تنتظر المشتريات التي تزيد عن المبلغ اعتمادًا واحدًا قبل ترحيلها.',
    appliesWhen: aboveThreshold,
    params: [threshold('1000.000')],
    slots: [slot('approver', 'Approvers', 'المعتمدون', ANY_ONE_HINT.en, ANY_ONE_HINT.ar)],
    steps: [
      { key: 'approval', en: 'Approval', ar: 'الاعتماد', slot: 'approver', completion: 'ANY', when: null },
    ],
  },
  {
    key: 'purchase-two-levels',
    subject: 'purchase',
    en: 'Two levels: manager, then finance',
    ar: 'مستويان: المدير ثم المالية',
    descriptionEn:
      'A manager reviews first, then finance approves. The person who approved the first level cannot approve the second.',
    descriptionAr:
      'يراجع المدير أولًا ثم تعتمد المالية. لا يمكن لمن اعتمد المستوى الأول أن يعتمد المستوى الثاني.',
    appliesWhen: aboveThreshold,
    params: [threshold('1000.000')],
    slots: [
      slot('manager', 'Managers', 'المديرون', ANY_ONE_HINT.en, ANY_ONE_HINT.ar),
      slot('finance', 'Finance approvers', 'معتمدو المالية', ANY_ONE_HINT.en, ANY_ONE_HINT.ar),
    ],
    steps: [
      {
        key: 'manager-review',
        en: 'Manager review',
        ar: 'مراجعة المدير',
        slot: 'manager',
        completion: 'ANY',
        when: null,
      },
      {
        key: 'finance-approval',
        en: 'Finance approval',
        ar: 'اعتماد المالية',
        slot: 'finance',
        completion: 'ANY',
        when: null,
      },
    ],
  },
  {
    key: 'purchase-tiered',
    subject: 'purchase',
    en: 'By amount: manager, and finance above a higher amount',
    ar: 'حسب المبلغ: المدير، والمالية فوق مبلغ أعلى',
    descriptionEn:
      'A manager approves purchases above the first amount. Above the second amount, finance approves as well.',
    descriptionAr: 'يعتمد المدير المشتريات التي تزيد عن المبلغ الأول. وفوق المبلغ الثاني تعتمد المالية أيضًا.',
    appliesWhen: aboveThreshold,
    params: [
      threshold('500.000'),
      {
        key: 'financeAbove',
        en: 'Finance also approves above',
        ar: 'تعتمد المالية أيضًا فوق',
        default: '5000.000',
        above: 'threshold',
      },
    ],
    slots: [
      slot('manager', 'Managers', 'المديرون', ANY_ONE_HINT.en, ANY_ONE_HINT.ar),
      slot('finance', 'Finance approvers', 'معتمدو المالية', ANY_ONE_HINT.en, ANY_ONE_HINT.ar),
    ],
    steps: [
      {
        key: 'manager-review',
        en: 'Manager review',
        ar: 'مراجعة المدير',
        slot: 'manager',
        completion: 'ANY',
        when: null,
      },
      {
        key: 'finance-approval',
        en: 'Finance approval',
        ar: 'اعتماد المالية',
        slot: 'finance',
        completion: 'ANY',
        when: { field: 'total', operator: 'gt', param: 'financeAbove' },
      },
    ],
  },
  {
    key: 'purchase-committee',
    subject: 'purchase',
    en: 'Committee: everyone must approve',
    ar: 'لجنة: يجب أن يعتمد الجميع',
    descriptionEn: 'Large purchases need the approval of every committee member.',
    descriptionAr: 'تحتاج المشتريات الكبيرة إلى اعتماد كل أعضاء اللجنة.',
    appliesWhen: aboveThreshold,
    params: [threshold('10000.000')],
    slots: [
      slot(
        'committee',
        'Committee members',
        'أعضاء اللجنة',
        'Everyone in these roles, and everyone named here, must approve.',
        'يجب أن يعتمد كل من في هذه الأدوار وكل من حُدد هنا.',
      ),
    ],
    steps: [
      {
        key: 'committee',
        en: 'Committee approval',
        ar: 'اعتماد اللجنة',
        slot: 'committee',
        completion: 'ALL',
        when: null,
      },
    ],
  },
  {
    key: 'purchase-board-chain',
    subject: 'purchase',
    en: 'Board chain: executive director to treasurer',
    ar: 'تسلسل مجلس الإدارة: من المدير التنفيذي إلى أمين الصندوق',
    descriptionEn:
      'Four approvals in order: executive director, vice chairman, chairman, then treasurer. Each person approves once.',
    descriptionAr:
      'أربعة اعتمادات بالترتيب: المدير التنفيذي ثم نائب الرئيس ثم الرئيس ثم أمين الصندوق. يعتمد كل شخص مرة واحدة.',
    appliesWhen: aboveThreshold,
    params: [threshold('5000.000')],
    slots: [
      slot('executiveDirector', 'Executive director', 'المدير التنفيذي', ANY_ONE_HINT.en, ANY_ONE_HINT.ar),
      slot('viceChairman', 'Vice chairman', 'نائب الرئيس', ANY_ONE_HINT.en, ANY_ONE_HINT.ar),
      slot('chairman', 'Chairman', 'الرئيس', ANY_ONE_HINT.en, ANY_ONE_HINT.ar),
      slot('treasurer', 'Treasurer', 'أمين الصندوق', ANY_ONE_HINT.en, ANY_ONE_HINT.ar),
    ],
    steps: [
      {
        key: 'executive-director',
        en: 'Executive director',
        ar: 'المدير التنفيذي',
        slot: 'executiveDirector',
        completion: 'ANY',
        when: null,
      },
      {
        key: 'vice-chairman',
        en: 'Vice chairman',
        ar: 'نائب الرئيس',
        slot: 'viceChairman',
        completion: 'ANY',
        when: null,
      },
      { key: 'chairman', en: 'Chairman', ar: 'الرئيس', slot: 'chairman', completion: 'ANY', when: null },
      {
        key: 'treasurer',
        en: 'Treasurer',
        ar: 'أمين الصندوق',
        slot: 'treasurer',
        completion: 'ANY',
        when: null,
      },
    ],
  },
]

export function workflowTemplate(key: string): WorkflowTemplate | undefined {
  return WORKFLOW_TEMPLATES.find((t) => t.key === key)
}

/** New workflow from a template: its name, the slots filled with roles or users, and the amounts. */
export const workflowFromTemplateSchema = z
  .object({
    template: z.string().min(1).max(60),
    name: z.string().trim().min(1, 'Name the workflow').max(120),
    nameAr: optionalText(120),
    description: optionalText(1000),
    slots: z.record(z.string().max(40), z.array(workflowAssigneeSchema).max(WORKFLOW_MAX_ASSIGNEES)),
    params: z.record(z.string().max(40), moneySchema),
  })
  .strict()
export type WorkflowFromTemplateInput = z.input<typeof workflowFromTemplateSchema>

export interface TemplateFill {
  slots: Record<string, WorkflowAssignee[]>
  params: Record<string, string>
}

/**
 * The spec a filled template produces, or what is missing (`slots.<key>`, `params.<key>`). Shared by
 * the web (validation as you type) and the API (authoritative). References (do these roles and users
 * exist?) are checked by the API.
 */
export function instantiateTemplate(
  template: WorkflowTemplate,
  fill: TemplateFill,
): { spec: WorkflowSpecInput; problems: { path: string; message: string }[] } {
  const problems: { path: string; message: string }[] = []
  for (const s of template.slots) {
    if (!fill.slots[s.key]?.length)
      problems.push({ path: `slots.${s.key}`, message: 'Choose who approves this step' })
  }
  for (const key of Object.keys(fill.slots)) {
    if (!template.slots.some((s) => s.key === key))
      problems.push({ path: `slots.${key}`, message: 'Unknown slot' })
  }
  const amounts: Record<string, string> = {}
  for (const p of template.params) {
    const raw = fill.params[p.key]
    if (raw === undefined || !moneySchema.safeParse(raw).success) {
      problems.push({ path: `params.${p.key}`, message: 'Enter an amount' })
    } else if (compareMoney(raw, 0) <= 0) {
      problems.push({ path: `params.${p.key}`, message: 'The amount must be more than zero' })
    } else amounts[p.key] = toMoney(raw)
  }
  for (const key of Object.keys(fill.params)) {
    if (!template.params.some((p) => p.key === key))
      problems.push({ path: `params.${key}`, message: 'Unknown amount' })
  }
  for (const p of template.params) {
    const own = amounts[p.key]
    const floor = p.above ? amounts[p.above] : undefined
    if (own !== undefined && floor !== undefined && compareMoney(own, floor) <= 0) {
      problems.push({ path: `params.${p.key}`, message: 'This amount must be more than the first amount' })
    }
  }
  const condition = (rule: WorkflowTemplateRule | null): WorkflowCondition | null =>
    rule
      ? {
          kind: 'group',
          match: 'all',
          negate: false,
          children: [
            { kind: 'rule', field: rule.field, operator: rule.operator, value: amounts[rule.param] ?? null },
          ],
        }
      : null
  const spec: WorkflowSpecInput = {
    conditions: condition(template.appliesWhen),
    steps: template.steps.map((s) => ({
      key: s.key,
      name: s.en,
      nameAr: s.ar,
      when: condition(s.when),
      assignees: dedupe(fill.slots[s.slot] ?? []),
      completion: s.completion,
      excludePriorApprovers: true,
      reminderAfter: null,
      dueAfter: null,
    })),
    onApproved: { action: null },
  }
  return { spec, problems }
}

function dedupe(assignees: WorkflowAssignee[]): WorkflowAssignee[] {
  const seen = new Set<string>()
  return assignees.filter((a) => {
    const k = assigneeKey(a)
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
}
